import React, { useState } from "react";
import "./document-name-dialog.scoped.scss";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import Button from "@material-ui/core/Button";
import CheckIcon from "@material-ui/icons/Check";
import CircularProgress from "@material-ui/core/CircularProgress";
import { TextField, Typography } from "@material-ui/core";
import { isValidLength, showPopupToastMsg } from "../../helpers";
import { getFileInfo } from "../../helpers/validate-file";
import { LookupsService } from "../../services/lookups/lookups.service";
const maxChar = 50;

type UploadedFile = {
  fileName: string;
  type: string;
  size: string;
  url: string;
};

interface DocumentNameDialogProps {
  file: File;
  show: boolean;
  path: string;
  segment?: string;
  onClose: (value: any) => void;
}

export const DocumentNameDialog: React.FC<DocumentNameDialogProps> = ({
  file,
  show,
  path,
  segment,
  onClose,
}) => {
  const { t } = useTranslation();
  const lookupsService = new LookupsService();

  const [name, setName] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const handleClose = (value: any) => onClose(value);

  const submit = async () => {
    if (!canSubmit()) return;
    setSaving(true);
    try {
      const { signedUrl, url } = await getSignedUrl(file);
      await lookupsService.uploadToSignedURL(signedUrl, file);
      const uploadedFile = formatFilePayload(file, url);
      const data = {
        ...uploadedFile,
        name,
      };
      handleClose(data);
    } catch (error) {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = (): boolean => {
    return !saving && isValidLength(name, maxChar) && file && Boolean(name);
  };

  // ---------- Services ----------
  const getSignedUrl = (file: File) => {
    const name = file.name;
    return lookupsService.requestSignedUrl(name, path, segment);
  };

  // ---------- Utils ----------
  const formatFilePayload = (file: File, url: string): UploadedFile => {
    const type = getFileInfo(file.type).fileType;
    const fileName = file.name.replace(new RegExp(`\\.${type}$`), "");
    const size = `${(file.size / 1024 / 1000).toFixed(2)} MB`;

    return {
      fileName,
      type,
      size,
      url,
    };
  };

  return (
    <Modal
      show={show}
      onHide={handleClose.bind(null, false)}
      centered
      dialogClassName="modal-md"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("DOCUMENT_NAME")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Typography variant="body1" className="mb-2">
          {t("FILE_NAME")}: {file.name}
        </Typography>

        <TextField
          fullWidth
          label={t("Enter Document Name")}
          onChange={(e: any) => setName(e.target.value)}
          value={name}
          name="document-name"
          variant={"filled"}
          disabled={saving}
          inputProps={{
            maxLength: 50,
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outlined"
          color="primary"
          className="btn-secondary-theme"
          size="small"
          onClick={handleClose.bind(null, false)}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="btn-primary-theme"
          onClick={submit}
          size="small"
          disabled={!name || saving}
        >
          {saving ? (
            <CircularProgress color="inherit" size={30} />
          ) : (
            <>
              <CheckIcon /> {t("save")}{" "}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
